import {
    useVariable as originalUseVariable,
    useVariableValue as originalUseVariableValue,
    DVCVariable,
    DVCVariableValue
} from '@devcycle/react-client-sdk'


export type UseVariableValue = <
    K extends string & keyof DVCVariableTypes
>(
    key: K,
    defaultValue: DVCVariableTypes[K]
) => DVCVariableTypes[K]

export const useVariableValue = originalUseVariableValue as UseVariableValue

export type UseVariable = <
    K extends string & keyof DVCVariableTypes,
    T extends DVCVariableValue & DVCVariableTypes[K],
>(
    key: K,
    defaultValue: DVCVariableTypes[K]
) => DVCVariable<T>

export const useVariable = originalUseVariable as UseVariable

type DVCJSON = { [key: string]: string | boolean | number }

export type DVCVariableTypes = {
    /**
        created by: Chris Parsons
        created on: 2025-05-15
    */
    'inline-filters': boolean
    /**
        created by: Hasseeb Hussain
        created on: 2025-05-13
    */
    'buyer-graph-ql': boolean
    /**
        created by: Fionn McConville
        created on: 2025-04-25
    */
    'max_parallel_document_uploads': number
    /**
        created by: Piotr Cwieczkowski
        created on: 2025-04-24
    */
    'prompt-key-override': string
    /**
        created by: Piotr Cwieczkowski
        created on: 2025-04-24
    */
    'llm-model': string
    /**
        created by: Piotr Cwieczkowski
        created on: 2025-04-23
    */
    'dedupe-result-chunks': boolean
    /**
        created by: Swetha Charles
        created on: 2025-04-09
    */
    'comments-email': boolean
    /**
        created by: Fionn McConville
        created on: 2025-04-07
    */
    'may-4th-activation': boolean
    /**
        created by: Archie Lamb
        created on: 2025-04-02
    */
    'market-analysis': boolean
    /**
        created by: Vishal Bhanderi
        created on: 2025-04-01
    */
    'keyword-search-function': boolean
    /**
        created by: Vishal Bhanderi
        created on: 2025-03-31
    */
    'semantic-search-result-count': number
    /**
        created by: Sashika Bamunurathna Mudiyanselage
        created on: 2025-03-28
    */
    'opportunity-page-redesign': boolean
    /**
        created by: Swetha Charles
        created on: 2025-03-20
    */
    'opportunity-notifications': boolean
    /**
        created by: Fionn McConville
        created on: 2025-03-20
    */
    'bid-self-serve': boolean
    /**
        created by: Dominic Bruton
        created on: 2025-03-17
    */
    'opportunity-buyer-activity': boolean
    /**
        created by: Chris Parsons
        created on: 2025-03-17
    */
    'onboarding-seo-redirect': boolean
    /**
        description: The number which controls how many qualifyBid questions we run in parallel
        created by: Fionn McConville
        created on: 2025-03-14
    */
    'qualify-bid-concurrency': number
    /**
        description: A list of variables that can be adjusted to improve LLM accuracy
        created by: Vishal Bhanderi
        created on: 2025-03-10
    */
    'bid-question-variables': DVCJSON
    /**
        created by: Archie Lamb
        created on: 2025-03-07
    */
    'procurement-act-lots': boolean
    /**
        created by: Vishal Bhanderi
        created on: 2025-02-27
    */
    'context-chunk-size': number
    /**
        created by: Archie Lamb
        created on: 2025-02-26
    */
    'procurement-act-timeline': boolean
    /**
        created by: Chris Parsons
        created on: 2025-02-24
    */
    'hubspot-payment-link': boolean
    /**
        created by: Archie Lamb
        created on: 2025-02-14
    */
    'procurement-act': boolean
    /**
        created by: Vishal Bhanderi
        created on: 2025-02-14
    */
    'notice-details-function': boolean
    /**
        created by: Dominic Bruton
        created on: 2025-02-13
    */
    'organisations-signal-filtering': boolean
    /**
        created by: Vishal Bhanderi
        created on: 2025-02-11
    */
    'bid-document-processor': string
    /**
        created by: Piotr Cwieczkowski
        created on: 2025-01-22
    */
    'load-notice-signals-async': boolean
    /**
        created by: Archie Lamb
        created on: 2025-01-16
    */
    'homepage-opportunities-pipeline': boolean
    /**
        created by: Fionn McConville
        created on: 2025-01-13
    */
    'allow-bid-requalify': boolean
    /**
        description: Keep the additional OpenAI function that allows the AI to search the full documents. Ideally going forward we want to disable this as it will hit token limits for OpenAI
        created by: Vishal Bhanderi
        created on: 2025-01-08
    */
    'enable-full-document-search': boolean
    /**
        created by: Fionn McConville
        created on: 2024-10-29
    */
    'new-highlight-logic': boolean
    /**
        created by: Chris Parsons
        created on: 2024-10-16
    */
    'process-notifications-job': boolean
    /**
        created by: Dominic Bruton
        created on: 2024-09-26
    */
    'calendar-reminders': boolean
    /**
        created by: Gabriella Abraham
        created on: 2024-09-18
    */
    'use-search-engine-for-notifications': boolean
    /**
        created by: Hasseeb Hussain
        created on: 2024-09-16
    */
    'signal-settings-improvements': boolean
    /**
        created by: Gabriella Abraham
        created on: 2024-08-16
    */
    'new-supplier-filter': boolean
    /**
        created by: Gabriella Abraham
        created on: 2024-08-16
    */
    'new-notices-search': boolean
    /**
        created by: Gabriella Abraham
        created on: 2024-07-22
    */
    'cpv-codes-filter': boolean
    /**
        created by: Gabriella Abraham
        created on: 2024-07-22
    */
    'keywords-on-the-fly': boolean
    /**
        created by: Gabriella Abraham
        created on: 2024-07-15
    */
    'layer-cake-filters': boolean
    /**
        created by: Justyna Gredecka
        created on: 2024-07-02
    */
    'documents-signal-score': boolean
    /**
        created by: Dominic Bruton
        created on: 2024-06-06
    */
    'notice-free-text-search': boolean
}

/**
    created by: Chris Parsons
    created on: 2025-05-15
*/
export const INLINE_FILTERS = 'inline-filters' as const

/**
    created by: Hasseeb Hussain
    created on: 2025-05-13
*/
export const BUYER_GRAPH_QL = 'buyer-graph-ql' as const

/**
    created by: Fionn McConville
    created on: 2025-04-25
*/
export const MAX_PARALLEL_DOCUMENT_UPLOADS = 'max_parallel_document_uploads' as const

/**
    created by: Piotr Cwieczkowski
    created on: 2025-04-24
*/
export const PROMPT_KEY_OVERRIDE = 'prompt-key-override' as const

/**
    created by: Piotr Cwieczkowski
    created on: 2025-04-24
*/
export const LLM_MODEL = 'llm-model' as const

/**
    created by: Piotr Cwieczkowski
    created on: 2025-04-23
*/
export const DEDUPE_RESULT_CHUNKS = 'dedupe-result-chunks' as const

/**
    created by: Swetha Charles
    created on: 2025-04-09
*/
export const COMMENTS_EMAIL = 'comments-email' as const

/**
    created by: Fionn McConville
    created on: 2025-04-07
*/
export const MAY_4TH_ACTIVATION = 'may-4th-activation' as const

/**
    created by: Archie Lamb
    created on: 2025-04-02
*/
export const MARKET_ANALYSIS = 'market-analysis' as const

/**
    created by: Vishal Bhanderi
    created on: 2025-04-01
*/
export const KEYWORD_SEARCH_FUNCTION = 'keyword-search-function' as const

/**
    created by: Vishal Bhanderi
    created on: 2025-03-31
*/
export const SEMANTIC_SEARCH_RESULT_COUNT = 'semantic-search-result-count' as const

/**
    created by: Sashika Bamunurathna Mudiyanselage
    created on: 2025-03-28
*/
export const OPPORTUNITY_PAGE_REDESIGN = 'opportunity-page-redesign' as const

/**
    created by: Swetha Charles
    created on: 2025-03-20
*/
export const OPPORTUNITY_NOTIFICATIONS = 'opportunity-notifications' as const

/**
    created by: Fionn McConville
    created on: 2025-03-20
*/
export const BID_SELF_SERVE = 'bid-self-serve' as const

/**
    created by: Dominic Bruton
    created on: 2025-03-17
*/
export const OPPORTUNITY_BUYER_ACTIVITY = 'opportunity-buyer-activity' as const

/**
    created by: Chris Parsons
    created on: 2025-03-17
*/
export const ONBOARDING_SEO_REDIRECT = 'onboarding-seo-redirect' as const

/**
    description: The number which controls how many qualifyBid questions we run in parallel
    created by: Fionn McConville
    created on: 2025-03-14
*/
export const QUALIFY_BID_CONCURRENCY = 'qualify-bid-concurrency' as const

/**
    description: A list of variables that can be adjusted to improve LLM accuracy
    created by: Vishal Bhanderi
    created on: 2025-03-10
*/
export const BID_QUESTION_VARIABLES = 'bid-question-variables' as const

/**
    created by: Archie Lamb
    created on: 2025-03-07
*/
export const PROCUREMENT_ACT_LOTS = 'procurement-act-lots' as const

/**
    created by: Vishal Bhanderi
    created on: 2025-02-27
*/
export const CONTEXT_CHUNK_SIZE = 'context-chunk-size' as const

/**
    created by: Archie Lamb
    created on: 2025-02-26
*/
export const PROCUREMENT_ACT_TIMELINE = 'procurement-act-timeline' as const

/**
    created by: Chris Parsons
    created on: 2025-02-24
*/
export const HUBSPOT_PAYMENT_LINK = 'hubspot-payment-link' as const

/**
    created by: Archie Lamb
    created on: 2025-02-14
*/
export const PROCUREMENT_ACT = 'procurement-act' as const

/**
    created by: Vishal Bhanderi
    created on: 2025-02-14
*/
export const NOTICE_DETAILS_FUNCTION = 'notice-details-function' as const

/**
    created by: Dominic Bruton
    created on: 2025-02-13
*/
export const ORGANISATIONS_SIGNAL_FILTERING = 'organisations-signal-filtering' as const

/**
    created by: Vishal Bhanderi
    created on: 2025-02-11
*/
export const BID_DOCUMENT_PROCESSOR = 'bid-document-processor' as const

/**
    created by: Piotr Cwieczkowski
    created on: 2025-01-22
*/
export const LOAD_NOTICE_SIGNALS_ASYNC = 'load-notice-signals-async' as const

/**
    created by: Archie Lamb
    created on: 2025-01-16
*/
export const HOMEPAGE_OPPORTUNITIES_PIPELINE = 'homepage-opportunities-pipeline' as const

/**
    created by: Fionn McConville
    created on: 2025-01-13
*/
export const ALLOW_BID_REQUALIFY = 'allow-bid-requalify' as const

/**
    description: Keep the additional OpenAI function that allows the AI to search the full documents. Ideally going forward we want to disable this as it will hit token limits for OpenAI
    created by: Vishal Bhanderi
    created on: 2025-01-08
*/
export const ENABLE_FULL_DOCUMENT_SEARCH = 'enable-full-document-search' as const

/**
    created by: Fionn McConville
    created on: 2024-10-29
*/
export const NEW_HIGHLIGHT_LOGIC = 'new-highlight-logic' as const

/**
    created by: Chris Parsons
    created on: 2024-10-16
*/
export const PROCESS_NOTIFICATIONS_JOB = 'process-notifications-job' as const

/**
    created by: Dominic Bruton
    created on: 2024-09-26
*/
export const CALENDAR_REMINDERS = 'calendar-reminders' as const

/**
    created by: Gabriella Abraham
    created on: 2024-09-18
*/
export const USE_SEARCH_ENGINE_FOR_NOTIFICATIONS = 'use-search-engine-for-notifications' as const

/**
    created by: Hasseeb Hussain
    created on: 2024-09-16
*/
export const SIGNAL_SETTINGS_IMPROVEMENTS = 'signal-settings-improvements' as const

/**
    created by: Gabriella Abraham
    created on: 2024-08-16
*/
export const NEW_SUPPLIER_FILTER = 'new-supplier-filter' as const

/**
    created by: Gabriella Abraham
    created on: 2024-08-16
*/
export const NEW_NOTICES_SEARCH = 'new-notices-search' as const

/**
    created by: Gabriella Abraham
    created on: 2024-07-22
*/
export const CPV_CODES_FILTER = 'cpv-codes-filter' as const

/**
    created by: Gabriella Abraham
    created on: 2024-07-22
*/
export const KEYWORDS_ON_THE_FLY = 'keywords-on-the-fly' as const

/**
    created by: Gabriella Abraham
    created on: 2024-07-15
*/
export const LAYER_CAKE_FILTERS = 'layer-cake-filters' as const

/**
    created by: Justyna Gredecka
    created on: 2024-07-02
*/
export const DOCUMENTS_SIGNAL_SCORE = 'documents-signal-score' as const

/**
    created by: Dominic Bruton
    created on: 2024-06-06
*/
export const NOTICE_FREE_TEXT_SEARCH = 'notice-free-text-search' as const
